import { api } from '@/lib/api'

import type {
	CustomPolicyCreatePayload,
	CustomPolicyCreateResponse,
	CustomPolicyDeleteResponse,
	PolicyDetails,
	PolicyList,
	PolicyOverview,
	PolicyUpdateResponse,
	PolicyValidationRequest,
	PolicyValidationResponse,
} from './types'

/**
 * Get the policies list
 * @param tenant - The tenant ID (use "all" for multi-tenant)
 * @param signal - Optional AbortSignal to abort the request
 * @param tenants - Optional tenant IDs for multi-tenant filtering
 */
export const getPolicyList = async (
	tenant: string,
	signal?: AbortSignal,
	tenants?: string[],
) => {
	const { data } = await api.get<PolicyList>(
		`/api/tenants/${tenant}/soar/rules`,
		{
			signal,
			tenants: tenants,
		},
	)

	return data
}

/**
 * Get the policy details
 * @param tenant - The tenant ID
 * @param policyId - The policy ID
 * @returns The policy details
 */
export const getPolicyDetails = async (
	tenant: string,
	policyId: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<PolicyDetails>(
		`/api/tenants/${tenant}/soar/rules/${policyId}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Update the policy
 * @param tenant - The tenant ID
 * @param policyId - The policy ID
 * @returns The updated policy
 */
export const updatePolicy = async (
	tenant: string,
	policyId: string,
	policy_data: Partial<PolicyOverview>,
) => {
	const { data } = await api.put<PolicyUpdateResponse>(
		`/api/tenants/${tenant}/soar/rules/${policyId}`,
		policy_data,
	)

	return data
}

/**
 * Create a custom policy.
 *
 * POST `.../soar/custom_policies` with envelope `{ name, enabled, create_alert,
 * rule }`. The backend forces `rule.type = "custom"` and overwrites `rule.name`;
 * never send `id`.
 *
 * @param tenant - The tenant ID
 * @param payload - The create envelope
 * @returns The created policy
 */
export const createCustomPolicy = async (
	tenant: string,
	payload: CustomPolicyCreatePayload,
) => {
	const { data } = await api.post<CustomPolicyCreateResponse>(
		`/api/tenants/${tenant}/soar/custom_policies`,
		payload,
	)

	return data
}

/**
 * Delete a custom policy.
 *
 * DELETE `.../soar/custom_policies/{id}`. Only valid for custom policies
 * (the backend rejects non-UUID/OOTB ids with a 400).
 *
 * @param tenant - The tenant ID
 * @param policyId - The custom policy ID
 * @returns The deleted policy id
 */
export const deleteCustomPolicy = async (tenant: string, policyId: string) => {
	const { data } = await api.delete<CustomPolicyDeleteResponse>(
		`/api/tenants/${tenant}/soar/custom_policies/${policyId}`,
	)

	return data
}

/**
 * Dry-run a custom-policy query (Test Detection Logic). POST — no abort signal
 * (POST cancellation causes silent UI failures). Always resolves with a result;
 * rule/OpenSearch failures arrive as `validated: false` + `error`.
 *
 * @param tenant - The tenant ID
 * @param body - The validation request body
 * @returns Validation outcome + impact counts
 */
export const validateCustomPolicy = async (
	tenant: string,
	body: PolicyValidationRequest,
) => {
	const { data } = await api.post<PolicyValidationResponse>(
		`/api/tenants/${tenant}/soar/custom_policies/validate`,
		body,
	)

	return data
}
